// src/games/darkforest/scenes/ruins.js
import { DIFFICULTY_CLASS } from '../../../game-system/skills/skillSystem';

export const ruins = {
  ruins_approach: {
    id: 'ruins_approach',
    title: "Approaching the Ruins",
    description: `The ancient path leads you deeper into the forest until you begin to glimpse weathered stone structures through the trees. As you get closer, you see the remains of what must have once been a magnificent temple complex, now largely reclaimed by the forest.

Crumbling columns rise from the undergrowth, and moss-covered stone walls delineate what were once grand chambers. Roots have pried apart foundation stones, and vines drape over carved archways. Despite the decay, there's an undeniable presence here - a lingering sense of reverence and power.

The main temple structure stands at the center of the ruins, its domed roof partially collapsed but still impressive. A wide staircase leads up to the entrance, which is framed by stone guardians worn smooth by centuries of weather.`,
    choices: [
      {
        id: 'enter_temple',
        text: "Climb the stairs and enter the temple",
        nextScene: 'temple_entrance',
        requires: null
      },
      {
        id: 'explore_ruins',
        text: "Explore the surrounding ruins first",
        nextScene: 'outer_ruins',
        requires: null
      },
      {
        id: 'observe',
        text: "Observe the ruins from a distance before proceeding",
        nextScene: 'observe_ruins',
        requires: null
      }
    ]
  },
  
  observe_ruins: {
    id: 'observe_ruins',
    title: "Observing the Ruins",
    description: `You hang back at the edge of the clearing, taking time to observe the ruins carefully before venturing closer. From this vantage point, you can see that the complex is larger than it first appeared, with partially collapsed structures extending into the surrounding forest.

The temple at the center seems to be aligned with the cardinal directions, with its main entrance facing east toward the rising sun. The architecture is unlike anything you've seen before, with flowing, organic lines that seem to mimic natural forms while still maintaining structural integrity.

There's no sign of recent human presence, but you notice birds nesting in the higher portions of the ruins, and small animals occasionally darting between collapsed walls.`,
    skillChecks: [
      {
        skill: 'perception',
        difficultyClass: DIFFICULTY_CLASS.MEDIUM,
        description: "Scan for any signs of danger or recent visitors",
        results: {
          success: "You notice what appears to be a thin tripwire across the temple entrance, likely connected to some kind of trap or alarm. You also spot several places where the ground has been disturbed recently, suggesting someone has visited these ruins not long ago.",
          rewards: {
            experience: 25,
            storyFlags: ['spotted_trap']
          },
          failure: "The ruins appear peaceful and abandoned. If there are any hazards or signs of other visitors, you don't spot them from this distance."
        }
      }
    ],
    choices: [
      {
        id: 'enter_temple',
        text: "Climb the stairs and enter the temple",
        nextScene: 'temple_entrance',
        requires: null
      },
      {
        id: 'explore_ruins',
        text: "Explore the surrounding ruins first",
        nextScene: 'outer_ruins',
        requires: null
      }
    ]
  },
  
  outer_ruins: {
    id: 'outer_ruins',
    title: "Exploring the Outer Ruins",
    description: `You decide to explore the structures surrounding the main temple before entering. What were once separate buildings now exist as partial walls, toppled columns, and cracked foundation stones emerging from the undergrowth.

Nature has reclaimed much of the complex, with trees growing through what were once floors and roots dislodging carefully laid stonework. Despite this, you can still make out what some of these buildings might have been - smaller shrines, living quarters, workshops.

As you explore, you discover fragments of carvings and statuary depicting robed figures with serene expressions, various forest animals, and the same celestial symbols you saw on the altar in the forest clearing.`,
    skillChecks: [
      {
        skill: 'investigation',
        difficultyClass: DIFFICULTY_CLASS.HARD,
        description: "Search the ruins thoroughly for anything valuable or informative",
        results: {
          success: "Behind a fallen column, you find a small, intact shrine that seems to have been protected from the worst of the weather. Inside is a weathered stone box containing a ceremonial dagger with a crystal blade, its hilt carved with symbols matching those on the forest altar.",
          rewards: {
            items: ['crystal_dagger'],
            experience: 35
          },
          failure: "While interesting from a historical perspective, your search reveals nothing of immediate value or use."
        }
      },
      {
        skill: 'history',
        difficultyClass: DIFFICULTY_CLASS.MEDIUM,
        description: "Determine the historical significance of these ruins",
        results: {
          success: "You recognize elements of the architecture and iconography as belonging to the ancient Sylvan people. This was likely a major temple complex dedicated to forest spirits and celestial cycles. The Sylvans were known for their harmony with nature and skill with a unique form of nature magic.",
          rewards: {
            experience: 30,
            storyFlags: ['sylvan_knowledge']
          },
          failure: "While clearly ancient and impressive, you can't determine the specific culture or time period that built these ruins."
        }
      }
    ],
    choices: [
      {
        id: 'enter_temple',
        text: "Head to the main temple now",
        nextScene: 'temple_entrance',
        requires: null
      },
      {
        id: 'check_shrine',
        text: "Examine a small shrine more carefully",
        nextScene: 'small_shrine',
        requires: null
      }
    ]
  },
  
  small_shrine: {
    id: 'small_shrine',
    title: "Ancient Shrine",
    description: `Among the ruins, you come across a small shrine that has remained remarkably intact. Its roof is still partially present, sheltering the interior from the worst of the elements. 

Inside, a stone altar similar to but smaller than the one in the forest clearing stands against the back wall. Above it, a circular window is positioned to catch the light of the full moon, directing it onto the altar. The walls are covered in elaborate murals, faded but still discernible, showing robed figures performing rituals around similar altars under moonlight.

On the altar sits a stone bowl, long dried out, and several niches that might have once held offerings or ritual items.`,
    skillChecks: [
      {
        skill: 'arcana',
        difficultyClass: DIFFICULTY_CLASS.MEDIUM,
        description: "Sense any lingering magic in the shrine",
        results: {
          success: "Despite its age, the shrine still resonates with magical energy. It appears to have been designed to collect and focus moonlight for ritual purposes, probably to enhance communion with forest spirits or for divination. Some of that magical potential remains, dormant but not dead.",
          rewards: {
            experience: 25
          },
          failure: "If there was ever magic here, it has long since faded away. The shrine now appears to be merely a historical relic."
        }
      }
    ],
    choices: [
      {
        id: 'place_offering',
        text: "Place an offering in one of the niches",
        nextScene: 'shrine_offering',
        requires: null
      },
      {
        id: 'enter_temple',
        text: "Head to the main temple now",
        nextScene: 'temple_entrance',
        requires: null
      }
    ]
  },
  
  shrine_offering: {
    id: 'shrine_offering',
    title: "Shrine Offering",
    description: `You decide to place an offering in one of the niches of the small shrine, following what appears to have been an ancient tradition here.`,
    inventory_choices: [
      {
        item_id: 'forest_amulet',
        text: "Place the forest amulet in a niche",
        result: {
          description: "You place the forest amulet in one of the niches. As soon as it settles into place, the symbols on the amulet begin to glow with a soft green light that spreads to the carvings on the altar and walls. The entire shrine seems to awaken, and you feel a gentle presence acknowledging your offering. A shaft of light appears, illuminating the altar despite the dense forest canopy above, and in that light, you see the spectral image of a map showing the interior of the main temple, with a secret chamber marked that wouldn't be visible from the main halls.",
          rewards: {
            experience: 40,
            storyFlags: ['temple_map_vision']
          }
        },
        nextScene: 'shrine_activated'
      },
      {
        item_id: 'minor_healing_potion',
        text: "Pour some healing potion into the bowl",
        result: {
          description: "You pour some of your healing potion into the dried stone bowl. The liquid begins to glow faintly and then evaporates, rising as a glowing mist that seems to be absorbed by the shrine itself. You feel a pleasant warmth spreading through your body, and notice that any minor injuries or fatigue you had have been relieved.",
          rewards: {
            healPercentage: 50,
            experience: 25
          }
        },
        nextScene: 'shrine_offering_accepted'
      },
      {
        item_id: 'torch',
        text: "Light a torch and place it in a holder",
        result: {
          description: "You light a torch and place it in what appears to be an ancient torch holder. The flame burns with unusual steadiness and brightness, illuminating the shrine's interior more thoroughly than expected. The light reveals faint text inscribed on the altar that would be invisible in normal lighting - instructions for a meditation ritual to commune with forest spirits.",
          rewards: {
            experience: 20,
            temporaryBonus: {
              skill: 'arcana',
              duration: 'medium',
              value: 2
            }
          }
        },
        nextScene: 'shrine_offering_accepted'
      }
    ],
    choices: [
      {
        id: 'no_offering',
        text: "Decide not to leave an offering after all",
        nextScene: 'shrine_no_offering',
        requires: null
      },
      {
        id: 'enter_temple',
        text: "Head to the main temple instead",
        nextScene: 'temple_entrance',
        requires: null
      }
    ]
  },
  
  shrine_no_offering: {
    id: 'shrine_no_offering',
    title: "No Offering",
    description: `You consider leaving an offering at the shrine, but ultimately decide against it. Perhaps you have nothing appropriate to give, or you're uncertain about the potential consequences of interacting with ancient magic.

The shrine remains silent and still, its secrets kept for another day or another visitor.`,
    choices: [
      {
        id: 'enter_temple',
        text: "Head to the main temple now",
        nextScene: 'temple_entrance',
        requires: null
      },
      {
        id: 'return_to_ruins',
        text: "Explore more of the outer ruins",
        nextScene: 'outer_ruins',
        requires: null
      }
    ]
  },
  
  shrine_offering_accepted: {
    id: 'shrine_offering_accepted',
    title: "Offering Accepted",
    description: `The shrine seems to accept your offering. There's a subtle shift in the atmosphere - a feeling of acknowledgment or appreciation from whatever lingering presence might still inhabit this place.

While no dramatic magical effects occur, you feel a sense of having shown proper respect to the ancient traditions, and perhaps having gained a measure of favor from the forest itself.`,
    choices: [
      {
        id: 'enter_temple',
        text: "Head to the main temple now",
        nextScene: 'temple_entrance',
        requires: null
      },
      {
        id: 'return_to_ruins',
        text: "Explore more of the outer ruins",
        nextScene: 'outer_ruins',
        requires: null
      }
    ]
  },
  
  shrine_activated: {
    id: 'shrine_activated',
    title: "Shrine Activated",
    description: `The ancient shrine comes alive with gentle magical energy in response to your offering. The spectral map shown in the shaft of light burns itself into your memory before fading away.

You now know of a hidden chamber deep within the main temple - a place that might hold the mysterious crystal you're seeking. The map revealed not only its location but also hinted at a mechanism to access it, involving three keystones placed in specific receptacles.

As the magical effect fades, you feel a deepened connection to this place and its forgotten traditions.`,
    choices: [
      {
        id: 'enter_temple',
        text: "Head to the main temple to find the secret chamber",
        nextScene: 'temple_entrance',
        requires: null
      },
      {
        id: 'return_to_ruins',
        text: "Explore more of the outer ruins first",
        nextScene: 'outer_ruins',
        requires: null
      }
    ]
  },
  
  temple_entrance: {
    id: 'temple_entrance',
    title: "Temple Entrance",
    description: `You climb the wide stone steps leading to the main temple. Despite centuries of exposure to the elements, the craftsmanship of these stairs is evident - each step perfectly level and fitted so precisely that little vegetation has managed to take root between them.

At the top of the stairs stands the temple entrance - a tall archway flanked by weathered statues of robed figures with serene expressions. The entrance leads into a shadowy interior, where columns can be seen supporting what remains of the ceiling.

The entrance itself is partially obstructed by fallen debris, but there's enough space to enter carefully.`,
    choices: [
      {
        id: 'enter_carefully',
        text: "Enter the temple carefully",
        nextScene: 'temple_interior',
        requires: null
      },
      {
        id: 'check_entrance',
        text: "Examine the entrance more carefully",
        nextScene: 'temple_entrance_examination',
        requires: 'spotted_trap'
      },
      {
        id: 'examine_statues',
        text: "Examine the guardian statues",
        nextScene: 'guardian_statues',
        requires: null
      }
    ],
    encounters: ['fallen_tree']
  },
  
  temple_entrance_examination: {
    id: 'temple_entrance_examination',
    title: "Examining the Entrance",
    description: `Having noticed the tripwire from a distance, you carefully examine the temple entrance for traps or other hazards. Sure enough, a thin cord is stretched across the entrance at ankle height, connected to a contraption rigged among the debris.

It appears to be recently placed, not an ancient defense mechanism. If triggered, it would cause additional stone blocks to fall from the weakened archway above - potentially injuring anyone passing underneath or blocking the entrance entirely.`,
    skillChecks: [
      {
        skill: 'sleightOfHand',
        difficultyClass: DIFFICULTY_CLASS.MEDIUM,
        description: "Disarm the trap",
        results: {
          success: "With careful, precise movements, you manage to disconnect the tripwire from its triggering mechanism without setting it off. The trap is now harmless, and you've preserved the evidence of someone else's presence here.",
          rewards: {
            experience: 30,
            storyFlags: ['trap_disarmed']
          },
          failure: "While attempting to disarm the trap, your fingers slip. You jerk back quickly enough to avoid triggering it fully, but a small portion of the rigged debris falls, making a loud noise that echoes through the ruins. The entrance remains passable, but anyone inside the temple will likely have heard your presence."
        }
      }
    ],
    choices: [
      {
        id: 'step_over',
        text: "Carefully step over the tripwire without disarming it",
        nextScene: 'temple_interior_cautious',
        requires: null
      },
      {
        id: 'enter_temple',
        text: "Enter the temple now that you're aware of the danger",
        nextScene: 'temple_interior',
        requires: 'trap_disarmed'
      }
    ]
  },
  
  guardian_statues: {
    id: 'guardian_statues',
    title: "Guardian Statues",
    description: `You take time to examine the weathered statues flanking the temple entrance. Despite centuries of exposure to the elements, their features remain discernible - robed figures with serene expressions, their hands held in positions that might represent specific ritual gestures.

Each statue stands about seven feet tall, carved from a single piece of stone with remarkable skill. They wear simple robes but elaborate headdresses depicting phases of the moon and various forest elements. Small recessed areas in their bases might once have held offerings.`,
    skillChecks: [
      {
        skill: 'investigation',
        difficultyClass: DIFFICULTY_CLASS.HARD,
        description: "Search the statues for any hidden features",
        results: {
          success: "Looking more closely at the base of the right-hand statue, you discover that one of the offering niches contains a hidden compartment. Inside is a small stone disk carved with the same symbols you've seen throughout the ruins - likely a ritual token of some kind.",
          rewards: {
            items: ['stone_key'],
            experience: 30
          },
          failure: "The statues appear to be purely decorative or ceremonial. If they contain any hidden features or compartments, you can't find them."
        }
      }
    ],
    choices: [
      {
        id: 'enter_temple',
        text: "Enter the temple",
        nextScene: 'temple_interior',
        requires: null
      },
      {
        id: 'check_entrance',
        text: "Examine the entrance itself more carefully",
        nextScene: 'temple_entrance_examination',
        requires: 'spotted_trap'
      }
    ]
  },
  
  temple_interior: {
    id: 'temple_interior',
    title: "Temple Interior",
    description: `You step through the archway into the temple interior. After the brightness outside, it takes your eyes a moment to adjust to the dim light filtering through gaps in the partially collapsed dome above.

The temple consists of a large central chamber with a circular design. Columns rise to support what remains of the domed ceiling, their surfaces carved with intricate patterns of vines and leaves. The floor is paved with stone tiles arranged in a spiral pattern leading to the center of the room, where a raised dais stands.

Several doorways lead off from the main chamber, some partially collapsed, others still accessible. Dust and debris cover most surfaces, but the air feels unexpectedly clean and carries a faint herbal scent.`,
    choices: [
      {
        id: 'approach_dais',
        text: "Approach the central dais",
        nextScene: 'central_dais',
        requires: null
      },
      {
        id: 'explore_northern_doorway',
        text: "Explore the northern doorway",
        nextScene: 'northern_chamber',
        requires: null
      },
      {
        id: 'explore_eastern_doorway',
        text: "Explore the eastern doorway",
        nextScene: 'eastern_chamber',
        requires: null
      },
      {
        id: 'explore_western_doorway',
        text: "Explore the western doorway",
        nextScene: 'western_chamber',
        requires: null
      }
    ]
  },
  
  temple_interior_cautious: {
    id: 'temple_interior_cautious',
    title: "Temple Interior - Cautious Entry",
    description: `You carefully step over the tripwire without disturbing it and enter the temple interior. After the brightness outside, it takes your eyes a moment to adjust to the dim light filtering through gaps in the partially collapsed dome above.

Moving quietly and remaining alert for further traps or signs of other visitors, you survey the large central chamber. The temple has a circular design with columns rising to support what remains of the domed ceiling. The floor is paved with stone tiles arranged in a spiral pattern leading to the center of the room, where a raised dais stands.

Several doorways lead off from the main chamber, some partially collapsed, others still accessible. Dust and debris cover most surfaces, but the air feels unexpectedly clean and carries a faint herbal scent. You notice what might be recent footprints in the dust leading toward the northern doorway.`,
    choices: [
      {
        id: 'approach_dais',
        text: "Approach the central dais",
        nextScene: 'central_dais',
        requires: null
      },
      {
        id: 'follow_footprints',
        text: "Follow the footprints to the northern doorway",
        nextScene: 'northern_chamber_alert',
        requires: null
      },
      {
        id: 'explore_eastern_doorway',
        text: "Explore the eastern doorway",
        nextScene: 'eastern_chamber',
        requires: null
      },
      {
        id: 'explore_western_doorway',
        text: "Explore the western doorway",
        nextScene: 'western_chamber',
        requires: null
      }
    ]
  }
};